import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, RotateCcw, Copy, Check, Download, Share2 } from "lucide-react";
import { Overline } from "../components/site/primitives";
import { generateEssenceCard } from "../components/site/essenceCard";

const API = process.env.REACT_APP_BACKEND_URL;
const MIN_CHARS = 80;

const LOADING_MESSAGES = [
  "Looking for the patterns beneath the words...",
  "Separating what you do from who you are...",
  "Finding the qualities people experience consistently...",
  "Distinguishing your Essence...",
];

const SECTIONS = [
  { key: "thread", title: "The Thread Running Through Your Essence" },
  { key: "underestimatedQuality", title: "What You May Not Realize About Yourself" },
  { key: "essenceVsPerformance", title: "Essence vs. Performance" },
  { key: "creationConnection", title: "Essence + Creation" },
];

const fade = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const LoadingState = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % LOADING_MESSAGES.length), 2600);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="essence-loading" className="flex min-h-[50vh] flex-col items-center justify-center text-center">
      <div className="h-px w-16 overflow-hidden bg-ink/10">
        <motion.div
          className="h-px w-16 bg-terracotta"
          animate={{ x: ["-100%", "100%"] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
      <AnimatePresence mode="wait">
        <motion.p
          key={idx}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.7 }}
          className="mt-10 max-w-lg font-display text-2xl font-medium italic leading-snug text-ink md:text-3xl"
        >
          {LOADING_MESSAGES[idx]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
};

const buildCopyText = (result) => {
  const words = result.essenceWords.map((w) => w.word.toUpperCase()).join(" · ");
  const lines = [
    "MY ESSENCE",
    words,
    "",
    ...result.essenceWords.map((w) => `${w.word.toUpperCase()}\n${w.explanation}`),
    "",
    ...SECTIONS.map((s) => `${s.title.toUpperCase()}\n${result[s.key]}`),
    "",
    "Discovered through Creating Wealth Through Community.",
  ];
  return lines.join("\n\n");
};

export default function Essence() {
  const [stage, setStage] = useState("input");
  const [feedback, setFeedback] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [cardUrl, setCardUrl] = useState(null);

  useEffect(() => {
    if (stage === "results" && result && !cardUrl) {
      generateEssenceCard(result.essenceWords.map((w) => w.word))
        .then(setCardUrl)
        .catch(() => setCardUrl(null));
    }
  }, [stage, result, cardUrl]);

  const downloadCard = () => {
    const a = document.createElement("a");
    a.href = cardUrl;
    a.download = "my-essence.png";
    a.click();
  };

  const shareCard = async () => {
    try {
      const blob = await (await fetch(cardUrl)).blob();
      const file = new File([blob], "my-essence.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Essence" });
        return;
      }
    } catch (e) {
      if (e.name === "AbortError") return;
    }
    downloadCard();
  };

  const ready = feedback.trim().length >= MIN_CHARS;

  const submit = async () => {
    if (!ready) return;
    setError("");
    setStage("loading");
    try {
      const res = await fetch(`${API}/api/essence/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feedback }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong.");
      try {
        localStorage.setItem("cwc_essence_words", JSON.stringify(data.essenceWords.map((w) => w.word)));
      } catch {}
      setResult(data);
      setStage("results");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e.message || "We couldn't complete your Essence analysis. Please try again.");
      setStage("input");
    }
  };

  const restart = () => {
    setStage("input");
    setResult(null);
    setFeedback("");
    setError("");
    setCardUrl(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const copyResults = async () => {
    const text = buildCopyText(result);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div data-testid="essence-page" className="relative min-h-screen overflow-x-clip bg-ivory">
      <div className="grain-overlay" />
      <main className="relative mx-auto max-w-[860px] px-6 py-20 md:px-10 md:py-28">
        <AnimatePresence mode="wait">
          {stage === "input" && (
            <motion.div key="input" {...fade}>
              <Overline>Creating Wealth Through Community</Overline>
              <h1 className="mt-6 font-display text-4xl font-medium leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                Discover Your <span className="italic text-terracotta">Essence</span>
              </h1>
              <p className="mt-8 max-w-xl font-display text-xl italic leading-snug text-ink md:text-2xl">
                Your Essence isn't something you need to become. It's who you've been all along.
              </p>
              <p className="mt-6 max-w-xl font-sans text-lg font-light leading-relaxed text-stonegray">
                Paste the feedback you collected from the people who know you. We'll look beneath the adjectives,
                accomplishments and roles to find the deeper qualities they consistently experience in you.
              </p>

              <div className="mt-14">
                <label htmlFor="essence-feedback" className="font-display text-2xl font-medium text-ink">
                  Paste your Essence feedback
                </label>
                <p className="mt-3 max-w-xl font-sans text-sm font-light leading-relaxed text-stonegray">
                  Include everything you collected. Don't clean it up or decide what's important first. Repetition,
                  stories, adjectives and seemingly random comments can all be useful.
                </p>
                <textarea
                  id="essence-feedback"
                  data-testid="essence-input"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder={'"People tell me I\u2019m creative, strong, funny, that I make people feel comfortable, that I always find a way to make things happen..."'}
                  className="mt-6 min-h-[280px] w-full resize-y border border-ink/15 bg-alabaster/60 p-6 font-sans text-base font-light leading-relaxed text-ink placeholder:text-stonegray/60 focus:border-terracotta focus:outline-none"
                />
                {!ready && feedback.trim().length > 0 && (
                  <p data-testid="essence-input-hint" className="mt-3 font-sans text-xs font-light text-stonegray">
                    Keep going — a little more detail gives us enough to find the real patterns.
                  </p>
                )}
                {error && (
                  <p data-testid="essence-error" className="mt-4 border-l-2 border-terracotta pl-4 font-sans text-sm font-light text-ink">
                    {error}
                  </p>
                )}
                <button
                  data-testid="essence-submit-btn"
                  onClick={submit}
                  disabled={!ready}
                  className={`group mt-8 inline-flex items-center gap-3 px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] transition-[transform,background-color,opacity] duration-300 ${
                    ready
                      ? "bg-terracotta text-white hover:-translate-y-0.5 hover:bg-ink"
                      : "cursor-not-allowed bg-ink/10 text-ink/40"
                  }`}
                >
                  <span>Discover My Essence</span>
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.6} />
                </button>
              </div>
            </motion.div>
          )}

          {stage === "loading" && (
            <motion.div key="loading" {...fade}>
              <LoadingState />
            </motion.div>
          )}

          {stage === "results" && result && (
            <motion.div key="results" {...fade} data-testid="essence-results">
              <Overline>Your Essence</Overline>

              <div className="mt-10 space-y-3">
                {result.essenceWords.map((w, i) => (
                  <motion.h2
                    key={w.word}
                    data-testid={`essence-word-${i + 1}`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.2 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
                    className="font-display text-5xl font-medium uppercase leading-[0.95] tracking-tight text-ink sm:text-6xl md:text-7xl"
                  >
                    {w.word}
                  </motion.h2>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.4 }}
              >
                <div className="mt-16 space-y-10 border-t border-ink/10 pt-12">
                  {result.essenceWords.map((w) => (
                    <div key={w.word}>
                      <h3 className="font-display text-2xl font-medium uppercase tracking-wide text-terracotta">{w.word}</h3>
                      <p className="mt-3 max-w-2xl font-sans text-lg font-light leading-relaxed text-stonegray">{w.explanation}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-20 space-y-14">
                  {SECTIONS.map((s) => (
                    <div key={s.key}>
                      <h3 className="font-display text-2xl font-medium leading-snug text-ink md:text-3xl">{s.title}</h3>
                      <p className="mt-4 max-w-2xl font-sans text-lg font-light leading-relaxed text-stonegray">{result[s.key]}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-20 border-y border-ink/10 py-14 text-center">
                  <Overline>Your Essence</Overline>
                  <p data-testid="essence-summary-line" className="mt-6 font-display text-2xl font-medium uppercase tracking-wide text-ink md:text-3xl">
                    {result.essenceWords.map((w) => w.word).join(" · ")}
                  </p>
                  <p className="mx-auto mt-8 max-w-xl font-display text-xl italic leading-snug text-ink md:text-2xl">
                    You do not need to become these things.
                  </p>
                  <p className="mx-auto mt-4 max-w-xl font-sans text-base font-light leading-relaxed text-stonegray">
                    The invitation is to begin noticing where they have already been present throughout your life — and
                    what becomes possible when you create from them intentionally.
                  </p>
                </div>

                {cardUrl && (
                  <div data-testid="essence-card-section" className="mt-20">
                    <h3 className="font-display text-2xl font-medium leading-snug text-ink md:text-3xl">
                      Your Essence card
                    </h3>
                    <p className="mt-3 max-w-xl font-sans text-base font-light leading-relaxed text-stonegray">
                      A keepsake of your five words — save it, set it as a wallpaper, or share it with the people who
                      helped you find them.
                    </p>
                    <div className="mt-8 grid items-start gap-10 md:grid-cols-2">
                      <img
                        src={cardUrl}
                        alt="Your Essence card"
                        data-testid="essence-card-preview"
                        className="w-full max-w-sm shadow-[0_30px_70px_-30px_rgba(26,26,24,0.5)]"
                      />
                      <div className="flex flex-col items-start gap-4 md:pt-4">
                        <button
                          data-testid="essence-card-download-btn"
                          onClick={downloadCard}
                          className="group inline-flex items-center gap-3 bg-terracotta px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-ink"
                        >
                          <Download className="h-4 w-4" strokeWidth={1.6} />
                          <span>Download My Card</span>
                        </button>
                        <button
                          data-testid="essence-card-share-btn"
                          onClick={shareCard}
                          className="group inline-flex items-center gap-3 border border-ink/25 px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-ink transition-[transform,border-color,color] duration-300 hover:-translate-y-0.5 hover:border-terracotta hover:text-terracotta"
                        >
                          <Share2 className="h-4 w-4" strokeWidth={1.6} />
                          <span>Share My Card</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-12 flex flex-wrap items-center gap-4">
                  <button
                    data-testid="essence-restart-btn"
                    onClick={restart}
                    className="group inline-flex items-center gap-3 border border-ink/25 px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-ink transition-[transform,border-color,color] duration-300 hover:-translate-y-0.5 hover:border-terracotta hover:text-terracotta"
                  >
                    <RotateCcw className="h-4 w-4" strokeWidth={1.6} />
                    <span>Start Again</span>
                  </button>
                  <button
                    data-testid="essence-copy-btn"
                    onClick={copyResults}
                    className="group inline-flex items-center gap-3 bg-terracotta px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-ink"
                  >
                    {copied ? <Check className="h-4 w-4" strokeWidth={1.8} /> : <Copy className="h-4 w-4" strokeWidth={1.6} />}
                    <span>{copied ? "Copied" : "Copy My Essence Results"}</span>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
